// Module Paramètres : informations entreprise, compte, exercices, abonnement.
// Régime TVA et date de début d'activité protégés (lecture seule).
// Clôture d'exercice irréversible (report à nouveau automatique côté backend).
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AbonnementSection from "@/components/AbonnementSection";
import UtilisateursSection from "@/components/UtilisateursSection";
import ModulesSection from "@/components/ModulesSection";
import { roleEffectif } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Building2, UserCog, Boxes, CalendarRange, CreditCard, Lock, Loader2, ShieldAlert, Plus, Image, Trash2, CheckCircle2, DatabaseBackup, DownloadCloud, UploadCloud,
} from "lucide-react";

const SUPPORT = "contact@groupeaerhpolynesie.com";
const CHAMPS_ENTREPRISE = [
  ["raison_sociale", "Raison sociale"], ["forme_juridique", "Forme juridique"],
  ["nom_gerant", "Nom du gérant"], ["prenom_gerant", "Prénom du gérant"],
  ["adresse_rue", "Adresse"], ["adresse_ville", "Ville"], ["adresse_ile", "Île"],
  ["telephone", "Téléphone"], ["email_contact", "Email de contact"],
  ["numero_tahiti", "Numéro Tahiti"], ["numero_rcs", "Numéro RCS (facultatif)"],
  ["numero_employeur", "Numéro Employeur (facultatif)"], ["secteur_activite", "Secteur d'activité"],
];

export default function Parametres() {
  const { user, setCompany } = useAuth();
  const estGestionnaire = ["gerant", "administrateur"].includes(roleEffectif(user));
  const [form, setForm] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [exercices, setExercices] = useState([]);
  const [pwd, setPwd] = useState({ actuel: "", nouveau: "", confirme: "" });
  const [confirmCloture, setConfirmCloture] = useState(false);
  const [controle, setControle] = useState(null);
  const [controleEnCours, setControleEnCours] = useState(false);
  const [logoEnCours, setLogoEnCours] = useState(false);
  const [sauvegarde, setSauvegarde] = useState({ mdp: "", confirme: "" });
  const [sauvegardeEnCours, setSauvegardeEnCours] = useState(false);
  const [restauration, setRestauration] = useState({ fichier: null, mdp: "" });
  const [restaurationEnCours, setRestaurationEnCours] = useState(false);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const [c, e] = await Promise.all([api.get("/companies/me"), api.get("/exercices")]);
      setForm(c.data);
      setExercices(e.data || []);
    } catch {
      setForm(null);
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const maj = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const enregistrer = async () => {
    try {
      const payload = {
        raison_sociale: form.raison_sociale, forme_juridique: form.forme_juridique,
        adresse_rue: form.adresse_rue, adresse_ville: form.adresse_ville, adresse_ile: form.adresse_ile,
        telephone: form.telephone, email_contact: form.email_contact,
        nom_gerant: form.nom_gerant, prenom_gerant: form.prenom_gerant,
        numero_tahiti: form.numero_tahiti, numero_rcs: form.numero_rcs || null,
        numero_employeur: form.numero_employeur || null, secteur_activite: form.secteur_activite,
        regime_tva: form.regime_tva, assujetti_tva: form.assujetti_tva,
        date_debut_activite: form.date_debut_activite,
        telephone_mobile: form.telephone_mobile || null,
        site_web: form.site_web || null,
        numero_rm: form.numero_rm || null,
        banque: form.banque || null,
        rib: form.rib || null,
        iban: form.iban || null,
        bic: form.bic || null,
        nom_commercial: form.nom_commercial || null,
      };
      await api.put("/companies/me", payload);
      toast.success("Vos informations ont été mises à jour.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "La mise à jour a échoué.");
    }
  };

  const changerMotDePasse = async () => {
    if (pwd.nouveau.length < 8) return toast.error("Le nouveau mot de passe doit faire au moins 8 caractères.");
    if (pwd.nouveau !== pwd.confirme) return toast.error("La confirmation ne correspond pas.");
    try {
      await api.post("/auth/change-password", { mot_de_passe_actuel: pwd.actuel, nouveau_mot_de_passe: pwd.nouveau });
      toast.success("Mot de passe mis à jour avec succès.");
      setPwd({ actuel: "", nouveau: "", confirme: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec du changement de mot de passe.");
    }
  };

  const cloturer = async (annee) => {
    try {
      const { data } = await api.post(`/exercices/${annee}/cloturer`);
      toast.success(data.message);
      setConfirmCloture(false);
      charger();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "La clôture a échoué.");
    }
  };

  const chargerControle = async (annee) => {
    setControle(null);
    setControleEnCours(true);
    try {
      const { data } = await api.get(`/exercices/${annee}/controle-cloture`);
      setControle(data);
    } catch {
      toast.error("Impossible de charger les contrôles de clôture.");
    } finally {
      setControleEnCours(false);
    }
  };

  const ouvrirNouvel = async () => {
    try {
      const { data } = await api.post("/exercices/nouveau");
      toast.success(data.message);
      charger();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ouverture impossible.");
    }
  };

  const TYPES_LOGO = ["image/png", "image/jpeg", "image/webp"];
  const choisirLogo = (e) => {
    const fichier = e.target.files?.[0];
    e.target.value = "";  // permet de re-choisir le même fichier
    if (!fichier) return;
    if (!TYPES_LOGO.includes(fichier.type)) return toast.error("Formats acceptés : PNG, JPEG ou WebP.");
    if (fichier.size > 1_000_000) return toast.error("Image trop volumineuse (max 1 Mo).");
    const lecteur = new FileReader();
    lecteur.onload = async () => {
      setLogoEnCours(true);
      try {
        const { data } = await api.put("/companies/me/logo", { logo: lecteur.result });
        setForm(data);
        setCompany(data);
        toast.success("Logo enregistré — il apparaîtra sur tous vos documents PDF.");
      } catch (err) {
        toast.error(err?.response?.data?.detail || "L'enregistrement du logo a échoué.");
      } finally {
        setLogoEnCours(false);
      }
    };
    lecteur.readAsDataURL(fichier);
  };

  const supprimerLogo = async () => {
    setLogoEnCours(true);
    try {
      const { data } = await api.delete("/companies/me/logo");
      setForm(data);
      setCompany(data);
      toast.success("Logo supprimé.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "La suppression a échoué.");
    } finally {
      setLogoEnCours(false);
    }
  };

  const sauvegardeValide = sauvegarde.mdp.length >= 8 && sauvegarde.mdp === sauvegarde.confirme;

  const telechargerSauvegarde = async () => {
    setSauvegardeEnCours(true);
    try {
      const reponse = await api.post(
        "/sauvegarde/export",
        { mot_de_passe: sauvegarde.mdp },
        { responseType: "blob" },
      );
      const dispo = reponse.headers["content-disposition"] || "";
      const trouve = dispo.match(/filename="?([^"]+)"?/);
      const nom = trouve ? trouve[1] : "sauvegarde-fenua.json.gz";
      const url = window.URL.createObjectURL(new Blob([reponse.data], { type: "application/gzip" }));
      const lien = document.createElement("a");
      lien.href = url;
      lien.download = nom;
      document.body.appendChild(lien);
      lien.click();
      lien.remove();
      window.URL.revokeObjectURL(url);
      setSauvegarde({ mdp: "", confirme: "" });
      toast.success("Sauvegarde chiffrée téléchargée. Conservez votre mot de passe en lieu sûr.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "La génération de la sauvegarde a échoué.");
    } finally {
      setSauvegardeEnCours(false);
    }
  };

  const restaurationValide = restauration.fichier && restauration.mdp.length >= 1;

  const restaurerSauvegarde = async () => {
    if (!restaurationValide) return;
    setRestaurationEnCours(true);
    try {
      const formData = new FormData();
      formData.append("fichier", restauration.fichier);
      formData.append("mot_de_passe", restauration.mdp);
      const { data } = await api.post("/sauvegarde/restaurer", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setRestauration({ fichier: null, mdp: "" });
      toast.success("Sauvegarde restaurée avec succès. Vos données ont été remplacées.");
      // Rechargement pour refléter les données restaurées partout dans l'application.
      setTimeout(() => window.location.reload(), 1200);
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "La restauration a échoué. Vérifiez le fichier et le mot de passe.");
    } finally {
      setRestaurationEnCours(false);
    }
  };

  if (chargement) return <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-[#4A8A8A]" /></div>;
  if (!form) return <p className="py-16 text-center text-sm text-[#1D3D3D]/60">Impossible de charger vos paramètres.</p>;

  const dernier = exercices[0];
  const peutOuvrir = dernier && dernier.statut === "cloture";

  return (
    <div className="fenua-fade-up max-w-4xl" data-testid="page-parametres">
      <h1 className="text-3xl font-black tracking-tight text-[#1D3D3D]">Paramètres</h1>
      <p className="mt-1 text-sm text-[#1D3D3D]/70">Gérez votre entreprise, votre compte et vos exercices comptables.</p>

      {/* Mon entreprise */}
      <Carte titre="Mon entreprise" icone={Building2} testid="section-entreprise">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {CHAMPS_ENTREPRISE.map(([k, label]) => (
            <div key={k} className="space-y-1.5">
              <Label className="text-xs font-semibold text-[#1D3D3D]">{label}</Label>
              <Input data-testid={`champ-${k}`} value={form[k] || ""} onChange={(e) => maj(k, e.target.value)}
                className="bg-white border-[#4A8A8A]/30 h-9" />
            </div>
          ))}
        </div>
        {/* Champs protégés */}
        <div className="mt-4 rounded-lg bg-[#E8F4F4] border border-[#4A8A8A]/20 p-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <ChampProtege label="Régime TVA" valeur={form.regime_tva} testid="protege-regime_tva" />
          <ChampProtege label="Date de début d'activité" valeur={form.date_debut_activite} testid="protege-date_debut" />
          <p className="sm:col-span-2 text-xs text-[#1D3D3D]/60 flex items-center gap-1.5">
            <ShieldAlert className="h-4 w-4" /> Pour modifier ces champs, contactez le support AERH : {SUPPORT}
          </p>
        </div>

        {/* Informations de facturation (reprises automatiquement sur les factures) */}
        <div className="mt-6 pt-5 border-t border-[#4A8A8A]/20">
          <h3 className="text-sm font-bold text-[#1D3D3D]">Informations de facturation</h3>
          <p className="mt-1 mb-3 text-xs text-[#1D3D3D]/60">
            Ces informations (facultatives) alimentent automatiquement vos factures client imprimables.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              ["telephone_mobile", "Téléphone portable"],
              ["site_web", "Site web"],
              ["nom_commercial", "Nom du commercial"],
              ["numero_rm", "Numéro RM"],
              ["banque", "Banque"],
              ["rib", "RIB"],
              ["iban", "IBAN"],
              ["bic", "BIC"],
            ].map(([k, label]) => (
              <div key={k} className="space-y-1.5">
                <Label className="text-xs font-semibold text-[#1D3D3D]">{label}</Label>
                <Input data-testid={`champ-${k}`} value={form[k] || ""} onChange={(e) => maj(k, e.target.value)}
                  className="bg-white border-[#4A8A8A]/30 h-9" />
              </div>
            ))}
          </div>
        </div>

        <div className="mt-4 flex justify-end">
          <Button data-testid="enregistrer-entreprise" onClick={enregistrer} className="bg-[#1D3D3D] hover:bg-[#2D5A5A] text-white">Enregistrer les modifications</Button>
        </div>
      </Carte>

      {/* Logo de l'entreprise */}
      <Carte titre="Logo de l'entreprise" icone={Image} testid="section-logo">
        <p className="text-sm text-[#1D3D3D]/70 mb-4">
          Ce logo apparaîtra automatiquement dans l'en-tête de tous vos documents PDF
          (états comptables, déclaration TVA, reçu d'arrêté de caisse).
        </p>
        <div className="flex items-center gap-5 flex-wrap">
          <div data-testid="logo-apercu"
            className="h-24 w-40 rounded-lg border border-[#4A8A8A]/30 bg-[#E8F4F4] flex items-center justify-center overflow-hidden">
            {form.logo ? (
              <img src={form.logo} alt="Logo" className="max-h-full max-w-full object-contain" />
            ) : (
              <span className="text-xs text-[#1D3D3D]/50">Aucun logo</span>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <label className="inline-flex">
              <input type="file" accept="image/png,image/jpeg,image/webp" data-testid="logo-input"
                onChange={choisirLogo} disabled={logoEnCours} className="hidden" />
              <span className={`inline-flex items-center cursor-pointer rounded-md px-4 py-2 text-sm font-medium text-white ${logoEnCours ? "bg-[#4A8A8A]/60" : "bg-[#1D3D3D] hover:bg-[#2D5A5A]"}`}>
                {logoEnCours ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Image className="h-4 w-4 mr-1" />}
                {form.logo ? "Remplacer le logo" : "Ajouter un logo"}
              </span>
            </label>
            {form.logo && (
              <Button data-testid="logo-supprimer" onClick={supprimerLogo} disabled={logoEnCours}
                variant="ghost" size="sm" className="text-[#8A4A4A] hover:bg-[#8A4A4A]/10 justify-start">
                <Trash2 className="h-4 w-4 mr-1" /> Supprimer le logo
              </Button>
            )}
            <p className="text-xs text-[#1D3D3D]/50">PNG, JPEG ou WebP · max 1 Mo.</p>
          </div>
        </div>
      </Carte>

      {/* Mon compte */}
      <Carte titre="Mon compte" icone={UserCog} testid="section-compte">
        <div className="space-y-1.5 max-w-md">
          <Label className="text-xs font-semibold text-[#1D3D3D]">Email de connexion</Label>
          <Input data-testid="email-connexion" value={user?.email || ""} disabled className="bg-[#E8F4F4] border-[#4A8A8A]/20 h-9" />
        </div>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl">
          <PwdInput label="Mot de passe actuel" testid="pwd-actuel" value={pwd.actuel} onChange={(v) => setPwd((p) => ({ ...p, actuel: v }))} />
          <PwdInput label="Nouveau mot de passe" testid="pwd-nouveau" value={pwd.nouveau} onChange={(v) => setPwd((p) => ({ ...p, nouveau: v }))} />
          <PwdInput label="Confirmer" testid="pwd-confirme" value={pwd.confirme} onChange={(v) => setPwd((p) => ({ ...p, confirme: v }))} />
        </div>
        <div className="mt-4 flex justify-end">
          <Button data-testid="changer-mot-de-passe" onClick={changerMotDePasse} className="bg-[#1D3D3D] hover:bg-[#2D5A5A] text-white">
            <Lock className="h-4 w-4 mr-1" /> Changer mon mot de passe
          </Button>
        </div>
      </Carte>

      {/* Exercices comptables */}
      <Carte titre="Exercices comptables" icone={CalendarRange} testid="section-exercices"
        action={peutOuvrir && (
          <Button data-testid="ouvrir-exercice" onClick={ouvrirNouvel} variant="secondary"
            className="bg-[#E8F4F4] text-[#1D3D3D] border border-[#4A8A8A]/20"><Plus className="h-4 w-4 mr-1" /> Ouvrir un nouvel exercice</Button>
        )}>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#E8F4F4] text-[#1D3D3D] text-left">
              <th className="px-3 py-2 font-semibold">Année</th>
              <th className="px-3 py-2 font-semibold">Période</th>
              <th className="px-3 py-2 font-semibold">Statut</th>
              <th className="px-3 py-2 font-semibold text-right">Écritures</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#4A8A8A]/10">
            {exercices.map((ex) => (
              <tr key={ex.annee} data-testid={`exercice-${ex.annee}`}>
                <td className="px-3 py-2 font-bold text-[#1D3D3D]">{ex.annee}</td>
                <td className="px-3 py-2 text-[#1D3D3D]/70">{ex.date_debut} → {ex.date_fin}</td>
                <td className="px-3 py-2">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${ex.statut === "cloture" ? "bg-[#1D3D3D] text-white" : "bg-[#7FE7C4]/30 text-[#1D3D3D]"}`}>
                    {ex.statut === "cloture" ? "Clôturé" : "Ouvert"}
                  </span>
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{ex.nb_ecritures}</td>
                <td className="px-3 py-2 text-right">
                  {ex.statut !== "cloture" && (
                    <AlertDialog onOpenChange={(ouvert) => { setConfirmCloture(false); if (ouvert) chargerControle(ex.annee); else setControle(null); }}>
                      <AlertDialogTrigger asChild>
                        <Button data-testid={`cloturer-${ex.annee}`} size="sm" variant="ghost" className="text-[#8A4A4A] hover:bg-[#8A4A4A]/10">Clôturer</Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent data-testid="dialog-cloture">
                        <AlertDialogHeader>
                          <AlertDialogTitle>Clôturer l'exercice {ex.annee} ?</AlertDialogTitle>
                          <AlertDialogDescription>
                            La clôture de l'exercice {ex.annee} est <strong>définitive</strong>. Toutes les écritures seront
                            verrouillées et le report à nouveau sera basculé automatiquement sur l'exercice suivant.
                          </AlertDialogDescription>
                        </AlertDialogHeader>

                        {/* Contrôles guidés avant clôture */}
                        <div data-testid="controle-cloture" className="rounded-lg border border-[#4A8A8A]/20 bg-[#E8F4F4]/50 p-3 text-sm space-y-2">
                          {controleEnCours || !controle ? (
                            <p className="text-[#1D3D3D]/60 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Vérifications en cours…</p>
                          ) : (
                            <>
                              <div className="flex items-center gap-2" data-testid="controle-equilibre">
                                {controle.equilibre
                                  ? <CheckCircle2 className="h-4 w-4 text-[#2D5A5A]" />
                                  : <ShieldAlert className="h-4 w-4 text-[#8A4A4A]" />}
                                <span className={controle.equilibre ? "text-[#1D3D3D]" : "text-[#8A4A4A] font-semibold"}>
                                  {controle.equilibre
                                    ? `Comptabilité équilibrée (${controle.nb_ecritures} écritures).`
                                    : `Comptabilité déséquilibrée — écart de ${controle.ecart} XPF.`}
                                </span>
                              </div>
                              {controle.journaux_desequilibres?.length > 0 && (
                                <p className="text-xs text-[#8A4A4A]">
                                  Journaux déséquilibrés : {controle.journaux_desequilibres.map((j) => j.journal).join(", ")}.
                                </p>
                              )}
                              {controle.anomalies?.length > 0 && (
                                <ul className="text-xs text-[#8A4A4A] list-disc pl-5" data-testid="controle-anomalies">
                                  {controle.anomalies.map((a, i) => <li key={i}>{a}</li>)}
                                </ul>
                              )}
                              {controle.cloture_possible && controle.anomalies?.length === 0 && (
                                <p className="text-xs text-[#2D5A5A]">Aucune anomalie détectée. La clôture est possible.</p>
                              )}
                            </>
                          )}
                        </div>

                        <label className="flex items-center gap-2 text-sm text-[#1D3D3D] py-2 cursor-pointer">
                          <input type="checkbox" data-testid="confirm-cloture-check" checked={confirmCloture}
                            disabled={!controle?.cloture_possible}
                            onChange={(e) => setConfirmCloture(e.target.checked)} className="h-4 w-4 accent-[#8A4A4A] disabled:opacity-40" />
                          Je confirme la clôture définitive de l'exercice {ex.annee}
                        </label>
                        <AlertDialogFooter>
                          <AlertDialogCancel data-testid="annuler-cloture">Annuler</AlertDialogCancel>
                          <AlertDialogAction data-testid="confirmer-cloture" disabled={!confirmCloture || !controle?.cloture_possible}
                            onClick={(e) => { if (!confirmCloture || !controle?.cloture_possible) { e.preventDefault(); return; } cloturer(ex.annee); }}
                            className="bg-[#8A4A4A] hover:bg-[#6e3a3a] disabled:opacity-50">Clôturer définitivement</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Carte>

      {/* Abonnement */}
      <Carte titre="Abonnement" icone={CreditCard} testid="section-abonnement">
        <AbonnementSection />
      </Carte>

      {/* Utilisateurs (multi-utilisateurs — gérant/administrateur uniquement) */}
      {estGestionnaire && (
        <Carte titre="Utilisateurs" icone={UserCog} testid="section-utilisateurs">
          <UtilisateursSection />
        </Carte>
      )}

      {/* Modules optionnels (gérant/administrateur uniquement) */}
      {estGestionnaire && (
        <Carte titre="Modules optionnels" icone={Boxes} testid="section-modules">
          <ModulesSection />
        </Carte>
      )}

      {/* Sauvegarde de sécurité */}
      <Carte titre="Sauvegarde de sécurité" icone={DatabaseBackup} testid="section-sauvegarde">
        <p className="text-sm text-[#1D3D3D]/80">
          Téléchargez une sauvegarde chiffrée de l'intégralité de votre compte (écritures de tous
          les exercices, tiers, paramètres, déclarations TVA, arrêtés de caisse et rapprochements
          validés). Le fichier <strong>.json.gz</strong> est <strong>chiffré</strong> avec le mot de
          passe que vous choisissez ci-dessous — ce n'est pas un export comptable lisible.
        </p>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-[#1D3D3D]">Mot de passe de la sauvegarde (min. 8 caractères)</Label>
            <Input type="password" data-testid="sauvegarde-mdp" value={sauvegarde.mdp}
              onChange={(e) => setSauvegarde((s) => ({ ...s, mdp: e.target.value }))}
              className="bg-white border-[#4A8A8A]/30 h-9" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-[#1D3D3D]">Confirmer le mot de passe</Label>
            <Input type="password" data-testid="sauvegarde-mdp-confirme" value={sauvegarde.confirme}
              onChange={(e) => setSauvegarde((s) => ({ ...s, confirme: e.target.value }))}
              className="bg-white border-[#4A8A8A]/30 h-9" />
          </div>
        </div>
        {sauvegarde.mdp && sauvegarde.confirme && sauvegarde.mdp !== sauvegarde.confirme && (
          <p className="mt-2 text-xs text-[#8A4A4A]">Les deux mots de passe ne correspondent pas.</p>
        )}
        <div className="mt-4 rounded-lg bg-amber-50 border border-amber-200 p-3 flex items-start gap-2 text-sm text-amber-800">
          <ShieldAlert className="h-5 w-5 shrink-0 mt-0.5 text-amber-600" />
          <span data-testid="sauvegarde-avertissement">
            Si vous perdez ce mot de passe, votre sauvegarde sera irrécupérable. Conservez-le dans un endroit sûr.
          </span>
        </div>
        <div className="mt-4 flex justify-end">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button data-testid="sauvegarde-telecharger" disabled={!sauvegardeValide || sauvegardeEnCours}
                className="bg-[#1D3D3D] hover:bg-[#2D5A5A] text-white">
                {sauvegardeEnCours ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <DownloadCloud className="h-4 w-4 mr-1" />}
                Télécharger ma sauvegarde
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="dialog-sauvegarde">
              <AlertDialogHeader>
                <AlertDialogTitle>Confirmer le téléchargement de la sauvegarde</AlertDialogTitle>
                <AlertDialogDescription>
                  Votre sauvegarde sera chiffrée avec ce mot de passe. <strong>Si vous le perdez,
                  votre sauvegarde sera irrécupérable.</strong> Conservez-le dans un endroit sûr.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="sauvegarde-annuler">Annuler</AlertDialogCancel>
                <AlertDialogAction data-testid="sauvegarde-confirmer" onClick={telechargerSauvegarde}
                  className="bg-[#1D3D3D] hover:bg-[#2D5A5A] text-white">
                  J'ai noté mon mot de passe — télécharger
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>

        {/* Restauration d'une sauvegarde chiffrée */}
        <div className="mt-8 pt-6 border-t border-[#4A8A8A]/20">
          <h3 className="text-sm font-bold text-[#1D3D3D] flex items-center gap-2">
            <UploadCloud className="h-4 w-4 text-[#4A8A8A]" /> Restaurer une sauvegarde
          </h3>
          <p className="mt-2 text-sm text-[#1D3D3D]/80">
            Importez un fichier <strong>.json.gz</strong> précédemment exporté et saisissez le mot de passe
            utilisé lors de sa création. <strong className="text-[#8A4A4A]">Attention :</strong> la restauration
            remplace l'intégralité des données comptables actuelles de votre entreprise.
          </p>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-[#1D3D3D]">Fichier de sauvegarde (.json.gz)</Label>
              <input
                type="file"
                accept=".gz,application/gzip"
                data-testid="restauration-fichier"
                onChange={(e) => setRestauration((s) => ({ ...s, fichier: e.target.files?.[0] || null }))}
                className="block w-full text-sm text-[#1D3D3D] file:mr-3 file:rounded-md file:border-0 file:bg-[#E8F4F4] file:px-3 file:py-2 file:text-sm file:font-semibold file:text-[#1D3D3D] hover:file:bg-[#4A8A8A]/20"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-[#1D3D3D]">Mot de passe de la sauvegarde</Label>
              <Input type="password" data-testid="restauration-mdp" value={restauration.mdp}
                onChange={(e) => setRestauration((s) => ({ ...s, mdp: e.target.value }))}
                className="bg-white border-[#4A8A8A]/30 h-9" />
            </div>
          </div>
          <div className="mt-4 rounded-lg bg-[#8A4A4A]/5 border border-[#8A4A4A]/20 p-3 flex items-start gap-2 text-sm text-[#8A4A4A]">
            <ShieldAlert className="h-5 w-5 shrink-0 mt-0.5" />
            <span data-testid="restauration-avertissement">
              Cette opération est <strong>irréversible</strong> : vos écritures, factures, tiers et états actuels
              seront définitivement remplacés par ceux de la sauvegarde.
            </span>
          </div>
          <div className="mt-4 flex justify-end">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button data-testid="restauration-lancer" disabled={!restaurationValide || restaurationEnCours}
                  className="bg-[#8A4A4A] hover:bg-[#6e3b3b] text-white">
                  {restaurationEnCours ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <UploadCloud className="h-4 w-4 mr-1" />}
                  Restaurer cette sauvegarde
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="dialog-restauration">
                <AlertDialogHeader>
                  <AlertDialogTitle>Confirmer la restauration</AlertDialogTitle>
                  <AlertDialogDescription>
                    Toutes les données comptables actuelles de votre entreprise seront
                    <strong> définitivement remplacées</strong> par celles de la sauvegarde. Cette action est
                    irréversible. Souhaitez-vous continuer ?
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="restauration-annuler">Annuler</AlertDialogCancel>
                  <AlertDialogAction data-testid="restauration-confirmer" onClick={restaurerSauvegarde}
                    className="bg-[#8A4A4A] hover:bg-[#6e3b3b] text-white">
                    Oui, remplacer mes données
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </Carte>
    </div>
  );
}

const Carte = ({ titre, icone: Icone, action, testid, children }) => (
  <section data-testid={testid} className="mt-6 bg-white border border-[#4A8A8A]/20 rounded-lg p-5">
    <div className="flex items-center justify-between mb-4">
      <h2 className="flex items-center gap-2 text-lg font-bold text-[#1D3D3D]"><Icone className="h-5 w-5 text-[#4A8A8A]" /> {titre}</h2>
      {action}
    </div>
    {children}
  </section>
);

const ChampProtege = ({ label, valeur, testid }) => (
  <div>
    <Label className="text-xs font-semibold text-[#1D3D3D]">{label} <Lock className="inline h-3 w-3 text-[#4A8A8A]" /></Label>
    <p data-testid={testid} className="text-sm font-medium text-[#1D3D3D] mt-1">{valeur || "—"}</p>
  </div>
);

const Info = ({ label, valeur }) => (
  <div>
    <p className="text-xs font-semibold uppercase tracking-wide text-[#4A8A8A]">{label}</p>
    <p className="text-[#1D3D3D] font-medium">{valeur}</p>
  </div>
);

const PwdInput = ({ label, value, onChange, testid }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-semibold text-[#1D3D3D]">{label}</Label>
    <Input type="password" data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="bg-white border-[#4A8A8A]/30 h-9" />
  </div>
);
